import {
  GraphQLErrorCode,
  reportGraphQLError,
  reportNoData,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Query } from 'react-apollo';

import {
  CatalogSelectorDropdownCountsQuery,
  CatalogSelectorDropdownCountsQueryVariables,
} from '../../../gen-src/queries/CatalogSelectorDropdownCountsQuery';
import { CatalogSelectorDropdownCountsQueryGql } from '../../queries/CatalogSelectorDropdownCountsQueryGql';
import { renderQueryResult } from '../renderQueryResult';
import {
  CatalogBrowserCounts,
  WebClientApiContentTypeCountsResponse,
} from './CatalogBrowserTypes';

export type CatalogBrowserCountsQueryWrapperProps = {
  readonly textSearch: string | null;
  readonly hasActiveWarning: boolean | null;
  readonly isCertified: boolean | null;
  readonly children: (result: CatalogBrowserCounts) => React.ReactNode;
  readonly webClientResult?: WebClientApiContentTypeCountsResponse;
};

export class CatalogBrowserCountsQueryWrapper extends React.Component<
  CatalogBrowserCountsQueryWrapperProps
> {
  public render(): React.ReactNode {
    return (
      <Query<
        CatalogSelectorDropdownCountsQuery,
        CatalogSelectorDropdownCountsQueryVariables
      >
        query={CatalogSelectorDropdownCountsQueryGql}
        variables={{
          textSearch: this.props.textSearch,
          hasActiveWarning: this.props.hasActiveWarning,
          isCertified: this.props.isCertified,
        }}
      >
        {(result) =>
          renderQueryResult(result, {
            renderLoading: this.renderChildrenWithEmptyCounts,
            renderData: this.renderChildrenWithData,
            renderNoData: this.renderNoData,
            renderDataWithError: this.renderDataWithError,
            renderError: this.renderError,
          })
        }
      </Query>
    );
  }

  private renderWebClientResponse = (
    response: WebClientApiContentTypeCountsResponse
  ): React.ReactNode => {
    if (response.type === 'SUCCESS') {
      return this.props.children({
        databasesCount: response.databasesCount,
        tablesCount: response.tablesCount,
      });
    } else {
      return this.props.children({
        databasesCount: null,
        tablesCount: null,
      });
    }
  };

  private renderChildrenWithEmptyCounts = (): React.ReactNode => {
    return this.props.webClientResult
      ? this.renderWebClientResponse(this.props.webClientResult)
      : this.props.children({
          databasesCount: null,
          tablesCount: null,
        });
  };

  private readonly renderChildrenWithData = (
    data: CatalogSelectorDropdownCountsQuery
  ): React.ReactNode => {
    return this.props.webClientResult
      ? this.renderWebClientResponse(this.props.webClientResult)
      : this.props.children({
          databasesCount: data.databasesConnection
            ? data.databasesConnection.totalCount
            : null,
          tablesCount: data.databaseTablesConnection
            ? data.databaseTablesConnection.totalCount
            : null,
        });
  };

  private readonly renderNoData = (): React.ReactNode => {
    return this.props.webClientResult ? (
      this.renderWebClientResponse(this.props.webClientResult)
    ) : (
      <>
        {reportNoData()}
        {this.renderChildrenWithEmptyCounts()}
      </>
    );
  };

  private readonly renderDataWithError = (
    data: CatalogSelectorDropdownCountsQuery,
    errorCode: GraphQLErrorCode | null
  ): React.ReactNode => {
    return (
      <>
        {reportGraphQLError(errorCode)}
        {this.props.webClientResult
          ? this.renderWebClientResponse(this.props.webClientResult)
          : this.renderChildrenWithData(data)}
      </>
    );
  };

  private readonly renderError = (
    errorCode: GraphQLErrorCode | null
  ): React.ReactNode => {
    return (
      <>
        {reportGraphQLError(errorCode)}
        {this.props.webClientResult
          ? this.renderWebClientResponse(this.props.webClientResult)
          : this.renderChildrenWithEmptyCounts()}
      </>
    );
  };
}
